import axios from "axios";
import { accessToken, accessTokenExpiry, bearer, checkAndRenewTokens, isLocalhost, oktaAuth, userSessionExpiry } from "../auth/authUtils";

/**
 * Returns the appropriate Content-Type header based on the type of data.
 *
 * @param {FormData|string|object} data - The data to determine the Content-Type for.
 * @returns {string} The Content-Type header value.
 *
 */
const getContentType = (data: FormData | string | object): string => {
  if (data instanceof FormData) {
    return "multipart/form-data";
  } else if (typeof data === "string" && data.startsWith("<")) {
    return "application/xml";
  } else {
    return "application/json";
  }
};

/**
 * Makes an API request to the specified URL with the given method and data.
 *
 * @template T - The type of data expected in the response.
 * @param {string} url - The URL of the API endpoint.
 * @param {string} method - The HTTP method to use (e.g. GET, POST, PUT, DELETE).
 * @param {FormData|string|object} [data] - The data to send with the request.
 * @returns {Promise<T>} A promise that resolves with the response data.
 *
 */
const apiRequest = async <T>(
  url: string,
  method: string,
  data?: FormData | string | object,
): Promise<T> => {
  const config: {
    url: string;
    method: string;
    headers: { [key: string]: string };
    data?: FormData | string | object;
  } = {
    url,
    method,
    headers: {},
  };

  if (method !== "GET" && data) {
    const contentType = getContentType(data);
    config.data = data;
    config.headers["Content-Type"] = contentType;
  }

  try {
    checkAndRenewTokens(oktaAuth , userSessionExpiry , accessTokenExpiry)
    if (!isLocalhost) {
      config.headers["Authorization"] = bearer + accessToken;
    }
    const response = await axios(config);
    return response.data;
  } catch (error: any) {
    const message = error?.response?.data?.message || `Error making API request: ${(error as Error).message}`
    throw new Error(message);
  }
};

export default apiRequest;
